import { useState } from "react";

import { toolsOf, type McpCatalog } from "../../api/mcp";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Card, Empty, FilterBar, Notice, SearchField, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { Resource } from "../../hooks/useResource";

/**
 * The tool catalog.
 *
 * Used to also show what each resolver would strand — MCP removed
 * `resolver_tools`/`resolver_report_dependents` along with the resolver-cap
 * guard itself (its own Postman collection now warns "NO 409 WILL STOP YOU").
 * There is no live data left to build that section from; see
 * `RESOLVER_GUARD_REMOVED` in `api/mcp.ts` for where that fact is said now —
 * the cap editor, at the moment a search_* tool is actually being capped.
 *
 * "What's registered" is the question this page still answers, so it lives
 * inside Tool caps rather than in a tab of its own.
 *
 * IT TAKES THE RESOURCE, IT DOES NOT FETCH. Tool caps needs the catalog for
 * itself, so a second `useResource` here meant one page running two
 * independent state machines over two endpoints — and a ready cap table could
 * contain a separate "could not load the catalog" failure, reading as if the
 * page itself had half-failed. One owner, one set of states.
 *
 * It also had an `embedded` flag and a standalone `<Surface>` heading for a
 * route that does not exist: `Caps` is its only caller and nothing routes it.
 * Both are gone rather than left as a path nobody takes.
 */
export function Catalog({
  state,
  onRetry,
}: {
  state: Resource<McpCatalog>;
  onRetry: () => void;
}) {
  const [q, setQ] = useState("");

  return (
    <>
      {state.kind === "loading" && <Loading what="the catalog" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the catalog" message={state.error.message} onRetry={onRetry} />
      )}
      {state.kind === "notfound" && <LoadFailed what="the catalog" message="Route not found." />}
      {state.kind === "invalid" && (
        <LoadFailed what="the catalog" message={state.error.message} />
      )}
      {state.kind === "error" && <LoadFailed what="the catalog" message={state.error.message} />}

      {state.kind === "ready" &&
        (() => {
          const c = state.data;
          const allTools = toolsOf(c);
          const tools = q
            ? allTools.filter((t) => t.toLowerCase().includes(q.toLowerCase()))
            : allTools;

          return (
            <>
              {/* Stat/StatRow, like every other figure in the console. */}
              <StatRow>
                <Stat value={allTools.length} label="Tools registered" />
              </StatRow>

              <Notice title="Capping a search_* tool">
                Report filters resolve UUIDs through these — capping one can strand every
                report that depends on it. MCP no longer refuses this write: nothing on
                either side will stop you, so treat a search_* cap as deliberate.
              </Notice>

              <Card
                title="All tools"
                aside={
                  <span className="font-mono text-xs tabular-nums whitespace-nowrap text-slate-500 dark:text-slate-400">
                    {tools.length} of {allTools.length}
                  </span>
                }
              >
                <FilterBar>
                  <SearchField
                    value={q}
                    onChange={setQ}
                    placeholder="Filter tools…"
                    label="Filter tools"
                  />
                </FilterBar>
                <div className="mt-4">
                  {tools.length === 0 ? (
                    <Empty>Nothing matches that filter.</Empty>
                  ) : (
                    <Table
                      className="[--gutter:0px]"
                      head={
                        <tr>
                          <Th>Tool</Th>
                        </tr>
                      }
                    >
                      {tools.map((t) => (
                        <tr key={t}>
                          <Td>
                            <span className="ref">{t}</span>
                          </Td>
                        </tr>
                      ))}
                    </Table>
                  )}
                </div>
              </Card>
            </>
          );
        })()}
    </>
  );
}
